import { OnboardingStatus, OnboardingStepType } from '@alice-financial/alice-sdk'
import { useNavigate } from 'react-router'
import { useOrg } from '../organization/useOrg'
import { useOrgId } from '../useOrgId'
import { buildOnboardingPath, getStep } from '../utils/onboardingWorkflowUtils'
import { getAllStepConfigs } from './stepConfiguration'

/**
 * Hook for managing onboarding navigation and step state
 */
export const useOnboardingNavigation = () => {
  const navigate = useNavigate()
  const orgId = useOrgId()
  const org = useOrg()

  const stepConfigs = getAllStepConfigs()

  /**
   * Navigate to a specific step
   */
  const navigateToStep = (stepType: OnboardingStepType) => {
    const targetPath = buildOnboardingPath(orgId, stepType)
    navigate(targetPath)
  }

  /**
   * Get the first incomplete step
   */
  const getFirstIncompleteStep = (): OnboardingStepType => {
    if (!org?.onboardingWorkflow) {
      return OnboardingStepType.OrderForm // Default to first step
    }

    for (const config of stepConfigs) {
      const step = getStep(org.onboardingWorkflow, config.stepType)
      if (!step || step.status !== OnboardingStatus.Completed) {
        return config.stepType
      }
    }

    // If all steps are complete, return the last step
    return stepConfigs[stepConfigs.length - 1]?.stepType || OnboardingStepType.OrderForm
  }

  /**
   * Navigate to the first incomplete step
   */
  const navigateToFirstIncompleteStep = () => {
    const firstIncompleteStep = getFirstIncompleteStep()
    navigateToStep(firstIncompleteStep)
  }

  return {
    getFirstIncompleteStep,
    navigateToFirstIncompleteStep,
  }
}
